import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  KeyboardEventHandler,
  memo,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import Input, { InputProps } from 'core/components/inputs/Input'
import { ID } from 'types/entity'
import { MenuProps, StandardOptionData } from '../../dialogs/Menu'
import { SelectProps } from '../Select'
import Popover from '../../dialogs/Popover'
import Combobox from '../Combobox'
import { buildOptionsList, buildOptionsMap } from '../helpers'
import MenuContent from './MenuContent'
import AutoSuggestControls from './AutoSuggestControls'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'
import cn from 'utils/cn'

export interface AutosuggesRef {
  comboboxElement: HTMLSpanElement | null
  inputElement: HTMLInputElement | null
  setInputValue: (value: string) => void
}

export interface AutosuggestProps<
  T extends { token_id: ID }
> extends SelectProps<T> {
  /**
   * Array of autosuggest options to display.
   */
  options: MenuProps<T>['options']
  /**
   * Content to display in the autosuggest footer.
   */
  footer?: MenuProps<T>['footer']
  /**
   * Content to display in the autosuggest header.
   */
  header?: MenuProps<T>['header']
  /**
   * Callback fired when the selected value changes.
   */
  onChange?: MenuProps<T>['onChange']
  /**
   * Callback fired when the input value changes.
   */
  onInputChange?: (value?: InputProps['value']) => void
  /**
   * The currently selected value(s).
   */
  value?: MenuProps<T>['value'] | null
  /**
   * Status indicator for async queries (loading, error, success, idle).
   */
  status?: 'idle' | 'loading' | 'pending' | 'success' | 'error'
  /**
   * Custom function to display option values.
   */
  getDisplayValue?: (
    option?:
      | string
      | number
      | StandardOptionData<T>
      | ReactElement<{ value: T['token_id']; onClick: VoidFunction }>
  ) => string | number | null | ReactNode
  /**
   * Custom render function for displaying selected tags.
   */
  renderTag?: (
    option?:
      | number
      | string
      | StandardOptionData<T>
      | ReactElement<{ value: T['token_id']; onClick: VoidFunction }>
  ) => ReactNode
  /**
   * Slot to display while loading options.
   */
  loadingSlot?: ReactElement
  /**
   * Slot to display when an error occurs.
   */
  errorSlot?: ReactElement
  /**
   * Slot to display when no options are available.
   */
  emptySlot?: ReactElement
  /**
   * If true, matches the dropdown width to the reference element.
   */
  matchReferenceWidth?: boolean
  /**
   * If true, allows clearing the selected value(s).
   */
  clearable?: boolean
  /**
   * Icon or element to display as a suffix.
   */
  suffix?: `${IconType}` | ReactElement
  /**
   * Icon or element to display when the dropdown is expanded.
   */
  dropdownExpandedIcon?: `${IconType}` | ReactElement
  /**
   * Visual style variant for the dropdown menu.
   */
  menuVariant?: MenuProps<T>['variant']
  /**
   * Optional id for blur override logic.
   */
  onBlurOverrideId?: string
  /**
   * If true, closes the dropdown after selecting an option.
   */
  closeOnSelect?: boolean
}

/**
 *
 * Autosuggest component supporting async queries, multi/single select, custom tags, and dropdown icons.
 * Integrates with forms and allows custom rendering, slots, and styling.
 */
const Autosuggest = forwardRef(
  <T extends { token_id: ID }>(
    {
      loadingSlot,
      emptySlot = (
        <div className="card bg-white flex flex-col justify-center items-center gap-2">
          <Icon name="search" color="text-dark-2" />
          <p className="text-sm text-center">No results match your search</p>
        </div>
      ),
      errorSlot = (
        <div className="card bg-white flex flex-col justify-center items-center gap-2">
          <Icon
            name="exclamation-mark"
            color="text-white bg-danger"
            className="rounded-full p-px"
            size={15}
          />
          <p className="text-sm text-center">
            Server error, try searching again
          </p>
        </div>
      ),
      status = 'idle',
      options = [],
      value = '',
      onChange,
      onInputChange,
      getDisplayValue = (v) => {
        if (!v) return null
        if (typeof v === 'string' || typeof v === 'number') return v
        if ('label' in v) return v.label
        return v.props.value
      },
      renderTag = (v) => {
        if (!v) return null
        if (typeof v === 'string' || typeof v === 'number') return v
        if ('label' in v) return v.label
        return v.props.value
      },
      suffix = 'chevron-down',
      dropdownExpandedIcon = 'chevron-up',
      disabled,
      readOnly,
      matchReferenceWidth = true,
      onFocus,
      onBlur,
      useTagOptions,
      name = 'autosuggest-input',
      menuVariant = 'default',
      clearable = false,
      onBlurOverrideId,
      closeOnSelect = true,
      footer,
      header,
      wrapperClassName,
      ...rest
    }: AutosuggestProps<T>,
    ref: Ref<AutosuggesRef>
  ) => {
    const [highlightedItem, setHighlightedItem] = useState(-1)
    const [menuExpanded, setMenuExpanded] = useState(false)
    const [inputValue, setInputValue] = useState<InputProps['value']>('')
    /**
     * This solves a caveat where input "jump" mid-way while an option is selected.
     * While the input have focus, prevent its textual value from being overridden, at the cost of de-syncing from global value.
     */
    const [acceptingOverride, setAcceptingOverride] = useState(true)
    const allowMultipleSelection = Array.isArray(value)
    const comboboxRef = useRef<HTMLSpanElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Expose both the HTML input element AND the setInputValue method
    useImperativeHandle(ref, () => {
      return {
        comboboxElement: comboboxRef.current,
        inputElement: inputRef.current,
        setInputValue
      }
    })

    const hashMap = useMemo(() => {
      return buildOptionsMap(options)
    }, [options])

    const hasValue = allowMultipleSelection ? value.length > 0 : !!value

    const selectedOptions = useMemo(() => {
      if (allowMultipleSelection) return value.map((v) => hashMap.get(v) ?? v)
      return [hashMap.get(value) ?? value]
    }, [allowMultipleSelection, hashMap, value])

    const onInputChangeCB = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value)
        onInputChange?.(e.target.value)
        if (closeOnSelect) setMenuExpanded(!readOnly)
        if (!useTagOptions && e.target.value) onChange?.(e.target.value)
      },
      [closeOnSelect, onChange, onInputChange, readOnly, useTagOptions]
    )
    const onMenuItemOnChangeCB = useCallback(
      (v: typeof value) => {
        onChange?.(v)
        if (useTagOptions) {
          onInputChangeCB({
            target: { value: '' }
          } as ChangeEvent<HTMLInputElement>)
        }
        if (closeOnSelect && menuExpanded) {
          setMenuExpanded(false)
        }
      },
      [menuExpanded, onChange, onInputChangeCB, useTagOptions, closeOnSelect]
    )
    const onFocusCB = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        onFocus?.(e)
        setAcceptingOverride(false)
        setMenuExpanded(!readOnly)
      },
      [onFocus, readOnly]
    )

    const handleClear = useCallback(() => {
      const newValue = allowMultipleSelection
        ? ([] as Array<T['token_id'] | string | number>)
        : null
      onChange?.(newValue)
      onInputChange?.('')
      setMenuExpanded(!menuExpanded)
    }, [allowMultipleSelection, menuExpanded, onChange, onInputChange])

    const showCloseIcon = useMemo(() => {
      if (readOnly || disabled) return false
      if (status === 'pending') return false
      if (status === 'error') return false
      if (!!(hasValue || inputValue) && clearable) return true
      if (!options?.length || options.length === 0) return false
      return !!(hasValue || inputValue) && menuExpanded && clearable
    }, [
      clearable,
      disabled,
      hasValue,
      inputValue,
      menuExpanded,
      options,
      readOnly,
      status
    ])

    const onBlurCB = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        if (document.activeElement?.id === onBlurOverrideId) return
        setAcceptingOverride(!allowMultipleSelection)
        onBlur?.(e)
        if (closeOnSelect) {
          setMenuExpanded(hasValue && clearable ? false : !menuExpanded)
        }
      },
      [
        allowMultipleSelection,
        clearable,
        closeOnSelect,
        hasValue,
        menuExpanded,
        onBlur,
        onBlurOverrideId
      ]
    )

    const listData = useMemo(
      () =>
        buildOptionsList(options ?? []).filter(
          (item) => !('groupLabel' in item)
        ),
      [options]
    )

    const handleKeyDown: KeyboardEventHandler = useCallback(
      (event) => {
        const { key } = event
        const listItemCount = listData.length
        if (highlightedItem >= listItemCount) return
        if (key === 'ArrowUp' && highlightedItem > 0) {
          setHighlightedItem((prev) => prev - 1)
          return
        }
        if (key === 'ArrowDown' && highlightedItem < listItemCount - 1) {
          setHighlightedItem((prev) => prev + 1)
          return
        }
        if (key === 'Enter' && highlightedItem >= 0) {
          event.stopPropagation()
          event.preventDefault()
          const { token_id, label } = listData[
            highlightedItem
          ] as StandardOptionData<T>
          onMenuItemOnChangeCB(token_id)
          if (typeof label === 'string') {
            setInputValue(label as string)
          }
        }
      },
      [highlightedItem, listData, onMenuItemOnChangeCB]
    )

    const suffixEl = useMemo(() => {
      if (readOnly || disabled) return undefined
      return (
        <AutoSuggestControls
          toggleMenu={() => {
            setMenuExpanded(!menuExpanded)
          }}
          enableClear={showCloseIcon}
          handleClear={handleClear}
          menuExpanded={menuExpanded}
          dropdownExpandedIcon={dropdownExpandedIcon}
          suffix={suffix}
          status={status}
        />
      )
    }, [
      readOnly,
      disabled,
      status,
      showCloseIcon,
      menuExpanded,
      dropdownExpandedIcon,
      suffix,
      handleClear
    ])

    const menuContent = useMemo(() => {
      if (status === 'pending' && loadingSlot) return loadingSlot
      if (status === 'error' && errorSlot) return errorSlot
      if ((!options?.length || options.length === 0) && emptySlot)
        return emptySlot
      return (
        <MenuContent
          status={status}
          options={options}
          value={value}
          menuVariant={menuVariant}
          onMenuItemOnChangeCB={onMenuItemOnChangeCB}
          useTagOptions={useTagOptions}
          highlightedItem={highlightedItem}
          footer={footer}
          header={header}
          getDisplayValue={
            getDisplayValue as (
              option?:
                | string
                | number
                | StandardOptionData<{ token_id: ID }>
                | React.ReactElement<
                    { value: ID; onClick: () => void },
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    string | React.JSXElementConstructor<any>
                  >
                | undefined
            ) => React.ReactNode
          }
        />
      )
    }, [
      footer,
      header,
      status,
      loadingSlot,
      errorSlot,
      options,
      emptySlot,
      value,
      menuVariant,
      onMenuItemOnChangeCB,
      useTagOptions,
      highlightedItem,
      getDisplayValue
    ])

    useEffect(() => {
      const cb = () => {
        setMenuExpanded(false)
      }
      document.addEventListener('click', cb)
      return () => {
        document.removeEventListener('click', cb)
      }
    }, [])

    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (
          useTagOptions &&
          e.key === 'Backspace' &&
          Array.isArray(value) &&
          value.length > 0 &&
          !inputValue
        ) {
          const tags = value
          tags.pop()
          onChange?.(tags)
        }
      }
      const element = inputRef.current
      element?.addEventListener('keydown', onKeyDown)
      return () => element?.removeEventListener('keydown', onKeyDown)
    }, [useTagOptions, inputValue, onChange, value])

    useEffect(() => {
      if (acceptingOverride && !useTagOptions) {
        const displayString =
          getDisplayValue?.(selectedOptions[0]) ?? value?.toString() ?? ''
        setInputValue(displayString as string)
      }
    }, [
      selectedOptions,
      getDisplayValue,
      value,
      acceptingOverride,
      useTagOptions
    ])

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn('flex', wrapperClassName)}
      >
        <Popover
          show={menuExpanded}
          position="bottom-left"
          useArrow={false}
          matchReferenceWidth={matchReferenceWidth}
          content={readOnly ? undefined : menuContent}
        >
          {allowMultipleSelection ? (
            <Combobox
              ref={comboboxRef}
              onFocus={onFocusCB}
              onBlur={onBlurCB}
              name={name}
              values={selectedOptions}
              suffix={suffixEl}
              onChange={onMenuItemOnChangeCB}
              //eslint-disable-next-line
              //@ts-ignore intentional type ignoring
              renderTag={renderTag}
              readOnly={readOnly}
              {...rest}
            >
              <Input
                ref={inputRef}
                autoComplete="off"
                variant="invisible"
                className="grow py-0.5 outline-0!"
                disabled={disabled}
                readOnly={readOnly}
                value={inputValue?.toString()}
                onFocus={onFocusCB}
                onBlur={onBlurCB}
                onChange={onInputChangeCB}
                placeholder={rest.placeholder}
              />
            </Combobox>
          ) : (
            <Input
              ref={inputRef}
              autoComplete="off"
              name={name}
              suffix={suffixEl}
              disabled={disabled}
              readOnly={readOnly}
              value={inputValue?.toString()}
              onFocus={onFocusCB}
              onBlur={(e) => setTimeout(() => onBlurCB(e))}
              onChange={onInputChangeCB}
              onKeyDown={handleKeyDown}
              {...rest}
            />
          )}
        </Popover>
      </div>
    )
  }
)

export default memo(Autosuggest)
